import {Controller} from '@hotwired/stimulus';
import {useHover, useIntersection} from 'stimulus-use';

/* stimulusFetch: 'lazy' */
export default class extends Controller {
    static targets = ['image', 'video', 'watchLater', 'channelIcon', 'gradient', 'watchFull', 'progressBar'];

    static values = {
        id: String,
        duration: {
            type: Number,
            default: 0,
        },
        focus: {
            type: Boolean,
            default: false,
        },
        touch: {
            type: Boolean,
            default: false,
        },
        auto: {
            type: Boolean,
            default: false,
        },
        sources: {
            type: Object,
            default: {},
        },
        speed: {
            type: Number,
            default: 1.0,
        },
    };

    playPromise = undefined;

    connect() {
        this.addAttributes();
        this.buildsources();
        if (this.focusValue && window.isMobile) {
            useIntersection(this, {
                threshold: 0.8,
                rootMargin: '-200px 0px -25% 0px',
                eventPrefix: false,
            });
        } else if (this.touchValue && window.isMobile) {
            this.boundTouchStart = this.touchStart.bind(this);

            this.element.addEventListener('touchstart', this.boundTouchStart);
        }

        if (!this.autoValue) {
            useHover(this, {element: this.element});
        }
    }

    disconnect() {
        if (this.boundTouchStart) {
            this.element.removeEventListener('touchstart', this.boundTouchStart);
        }

        this.stop();
    }

    touchStart() {
        this.dispatch('playing', {detail: {element: this.video}});
        this.show();
    }

    progressBarTargetConnected(e) {
        let currentTime = localStorage.getItem('vjs_resume-' + this.idValue);
        if (currentTime != null && this.durationValue > 0) {
            e.style.width = (currentTime / this.durationValue) * 100 + '%';
        }
    }

    videoTargetConnected(e) {
        e.addEventListener('playing', (e) => {
            this.hideimg(e.target.parentElement.querySelector('.preview-img'));
        });
        e.addEventListener('pause', (e) => {
            this.showimg(e.target.parentElement.querySelector('.preview-img'));
        });

        if (this.autoValue) {
            this.show();
        }
    }

    videoTargetDisonnected(e) {
        e.removeEventListener('playing', (e) => {
            this.hideimg(e.target.parentElement.querySelector('.preview-img'));
        });
        e.removeEventListener('pause', (e) => {
            this.showimg(e.target.parentElement.querySelector('.preview-img'));
        });

        e.pause();
    }

    imageTargetDisconnected(e) {
        this.showimg(e);
    }

    imageTargetConnected(e) {
        this.showimg(e);
    }

    showimg(i) {
        if (i && i.classList.contains('opacity-0')) {
            i.classList.remove('opacity-0');
        }
    }

    hideimg(i) {
        if (i && !i.classList.contains('opacity-0')) {
            i.classList.add('opacity-0');
        }
    }

    mouseEnter() {
        if (!window.isMobile) {
            this.show();
        }
    }

    mouseLeave() {
        if (!window.isMobile) {
            this.stop();
        }
    }

    playing(e) {
        if (e.detail.element !== this.video) {
            this.stop();
        }
    }

    appear() {
        if (!window.isPreviewRendered()) {
            this.show();
        }
    }

    disappear() {
        this.stop();
    }

    show() {
        if (this.hasGradientTarget) {
            this.gradientTarget.style.visibility = 'hidden';
        }

        if (this.hasWatchLaterTarget) {
            this.watchLaterTarget.classList.remove('hidden');
        }

        if (this.hasWatchFullTarget) {
            this.watchFullTarget.classList.remove('hidden');
        }

        this.play();
    }

    play() {
        if (!this.video) {
            return;
        }

        this.video.autoplay = true;
        this.video.muted = true;

        if (this.speedValue > 1.0) {
            this.video.playbackRate = this.speedValue;
        }

        this.video
            .play()
            .then(() => {
                this.playPromise = true;
                this.video.classList.remove('opacity-0');
            })
            .catch((error) => {
                // fail silently
                console.error(error);
            });
    }

    stop() {
        if (this.hasGradientTarget) {
            this.gradientTarget.classList.remove('hidden');
        }

        if (this.hasWatchLaterTarget) {
            this.watchLaterTarget.classList.add('hidden');
        }

        if (this.hasWatchFullTarget) {
            this.watchFullTarget.classList.add('hidden');
        }

        if (this.hasImageTarget) {
            this.imageTarget.classList.remove('opacity-0');
        }

        if (this.video) {
            this.video.autoplay = false;
            this.video.muted = true;
            this.video.classList.add('opacity-0');
            this.video.pause();
        }
    }

    addAttributes() {
        if (!this.video) {
            return;
        }

        this.video.setAttribute('preload', 'none');
        this.video.setAttribute('playsinline', '');
        this.video.setAttribute('disablepictureinpicture', '');
        this.video.setAttribute('disableremoteplayback', '');
        this.video.setAttribute('x-webkit-airplay', 'deny');
    }

    buildsources() {
        if (!this.video) {
            return;
        }

        const currentSources = this.video.querySelectorAll('source');
        if (currentSources.length > 0 || Object.keys(this.sourcesValue).length === 0) {
            return;
        }

        const mp4 = document.createElement('source');
        mp4.setAttribute(
            'src',
            this.sourcesValue[window.isMobile || !this.sourcesValue.mp4_large ? 'mp4' : 'mp4_large']
        );
        mp4.setAttribute('type', 'video/mp4');
        this.video.appendChild(mp4);
    }

    get video() {
        return this.element.querySelector('video');
    }
}
